// 11 §9.1 — ResolutionLog → AnimationPlan. Entries are grouped by stage in log order; within a
// stage, entries touching disjoint units and tiles play in parallel; entries sharing a unit or
// tile play sequentially in log order. The client never reorders. docs/56 EP1–EP2: only one blow
// lands at a time (walks may run beside it), with a beat after each blow and a longer one when
// the other side starts acting, so a pulse can be followed by eye.
import { ITEM_ABILITY_BASE } from '../items/catalogue';
import type { Cause, Effect, ResolutionEntry, ResolutionLog, Snapshot, Stage, StatusKind, Tile } from '../sim/types';
import { STAGE_ORDER } from '../sim/types';
import { isCrawlEffect } from '../crawl/crawl';
import { isExploreEffect } from '../explore/explore';
import { isSnareEntry } from '../snares/snares';
import { FINAL_FALL_MS, GROUP_GAP_MS, SIDE_GAP_MS, WARP_SQUARES, durationMs, tweenFor, type TweenSpec } from './catalogue';
import { foeLook, itemDefLook, lookFor, spellClipMs, type FoeCastKind, type SpellCast } from './spells';
import { crowSquares, flightMs, isFlight, type Carry } from './flight';

export type Speed = 1 | 2 | 4;
export interface AnimationPlan { stages: StageBlock[]; totalMs: number; speed: Speed; autoSped: boolean }
export interface StageBlock {
  stage: Stage;
  groups: Clip[][];   // groups play in parallel, in order
  /** docs/56 EP2: the pause after each group (ms at 1×), same length as `groups`. */
  gaps: number[];
  /** docs/56 EP2: the pause before this stage — the other side is about to act. */
  leadMs: number;
  /** Who acts in this stage: the party (and its friends) or the foes. */
  side: 'party' | 'foes';
  /** Nothing shows in this stage (foes held in stasis, statuses wearing off): no beat before it, no card. */
  idle: boolean;
}
export interface Clip {
  entry: ResolutionEntry; tween: TweenSpec; sound?: string; ms: number;
  /** docs/47: a spell's own clip — gathered, flown, landed — before the entries it caused. */
  spell?: SpellCast;
  /** Where a spell clip's impact falls (ms at 1×). */
  impactMs?: number;
  /** docs/47: this entry's server cues already played on the spell clip drawn for it (a foe's cast). */
  muted?: boolean;
  /** docs/47: a Blink's arrival — the figure is simply there, the spell clip showed the trip. */
  teleport?: boolean;
  /** docs/56 EP5: this drop leaves no hero standing — the defeat's slow fall under the camera. */
  finalFall?: boolean;
  /**
   * docs/102 FL2: a flier's move — it takes off, flies from origin `from` to origin `to` as the crow
   * flies and lands (the walk's squares are not drawn); FL5: carrying `carry` off with it.
   */
  flight?: { from: Tile; to: Tile; carry?: Carry };
  /** docs/102: an earlier flight already drew this entry (a flier's next bound, the figure it carried off): the figure is simply there. */
  drawn?: boolean;
}

export interface PlanOptions {
  speed?: Speed; reduceMotion?: boolean; reduceIntensity?: boolean;
  /** docs/47: the board as the pulse began (where casters and targets stand, the foes' people). */
  board?: Snapshot;
}

/** docs/56 EP3: a pulse longer than this plays at 2× unless the player chose a speed (raised with the slower clips). */
export const AUTO_2X_THRESHOLD_MS = 24_000;

/** Units and tiles an effect touches (footprint used for the parallel/serial decision). */
export function footprint(effect: Effect): { units: number[]; tiles: Tile[] } {
  switch (effect.kind) {
    case 'Moved': return { units: [effect.unit], tiles: effect.path };
    case 'Displaced': return { units: [effect.unit], tiles: [effect.from, effect.to] };
    case 'EventFired': case 'Interacted': return { units: [], tiles: effect.tiles };
    case 'Spawned': return { units: [effect.unit], tiles: [effect.tile] };
    case 'Other': return { units: effect.unit !== undefined ? [effect.unit] : [], tiles: effect.tiles };
    case 'Cast': return { units: [effect.unit], tiles: [] };
    // docs/52: a search round the searcher, a hidden thing showing, a trap springing or made safe
    case 'Searched': return { units: [effect.unit], tiles: effect.tiles };
    case 'Revealed': return { units: [], tiles: effect.tiles };
    case 'TrapSprung': case 'Disarmed': return { units: [effect.unit], tiles: [effect.tile] };
    // docs/60: the crawl
    case 'LairWoke': return { units: effect.units, tiles: [] };
    case 'Fell': return { units: [effect.unit], tiles: [effect.from, effect.to] };
    case 'Roused': case 'AreaEntered': case 'LoreRead': return { units: [effect.unit], tiles: [] };
    case 'ShrineAnswered': return { units: [effect.unit, ...effect.touched], tiles: [] };
    // docs/61: exploration
    case 'Discovered': case 'Glimpsed': case 'Blessed': return { units: [effect.unit], tiles: [] };
    case 'VeilParted': return { units: effect.by !== undefined ? [effect.by] : [], tiles: [] };
    case 'WaySighted': return { units: [effect.by], tiles: [] };
    case 'Departed': return { units: [], tiles: [] };
    // docs/66 RM10
    case 'Called': return { units: [effect.leader, ...effect.units], tiles: [] };
    case 'CallBroken': return { units: [effect.leader], tiles: [] };
    case 'Raised': return { units: [effect.caster, ...effect.units], tiles: [] };
    case 'RaiseBroken': return { units: [effect.caster], tiles: [] };
    case 'CaptiveFreed': return { units: [effect.unit], tiles: [] };
    // docs/118 PT14: the shout holds everyone it sends running; then the runners all run together
    // (their routes may share squares: each is off the board at its end, so they never meet)
    case 'Retreat': return { units: [...(effect.leader !== undefined ? [effect.leader] : []), ...effect.units], tiles: [] };
    case 'Left': return { units: [effect.unit], tiles: [] };
    // docs/103: the striker, the foe and the friend across all hold still for the flourish
    case 'Flanked': return { units: [effect.unit, effect.target, effect.with], tiles: [] };
    // docs/104: the paladin and the friend it shields; the friend an Encore hastens; the squares grown
    case 'Interposed': return { units: [effect.unit, effect.ward], tiles: [] };
    case 'Hastened': return { units: [effect.unit], tiles: [] };
    case 'TerrainSet': return { units: [], tiles: effect.tiles };
    case 'Reacted': return { units: [], tiles: effect.tiles };
    // docs/126
    case 'PouchLeft': return { units: [effect.unit], tiles: effect.tiles };
    case 'PickedUp': return { units: [effect.unit], tiles: [] };
    case 'Glanced': return { units: [effect.unit], tiles: [] };
    // docs/140: a trap's spring holds its victim and its foe; its acts hold the squares and units they touched
    case 'SnareSprung': return { units: [...(effect.victim !== undefined ? [effect.victim] : []), ...(effect.by !== undefined ? [effect.by] : [])], tiles: [effect.tile] };
    case 'SnareActed': return { units: effect.units, tiles: effect.tiles };
    case 'SnareEnded': return { units: [], tiles: [] };
    case 'SnareFound': return { units: [effect.unit], tiles: [] };
    case 'SnareDisarmed': return { units: [effect.unit], tiles: [effect.tile] };
    case 'Damaged': case 'Healed': case 'StatusSet': case 'StatusCleared': case 'Dropped': case 'Revived': case 'Held': case 'Swayed': case 'GearPower': case 'ItemUsed': case 'ResourceGained':
    case 'MoveUsed':
      return { units: [effect.unit], tiles: [] };
  }
}

function actorOf(entry: ResolutionEntry): number[] { return entry.actor !== undefined ? [entry.actor] : []; }

export function clipFor(entry: ResolutionEntry, opts: PlanOptions): Clip {
  const { spec, tiles } = tweenFor(entry.effect);
  let tween = spec;
  if (opts.reduceMotion) {
    // §9.3: slides become 60 ms per tile; lunges are skipped (flash only, numbers still appear)
    if (tween.kind === 'slide' || tween.kind === 'push') tween = { ...tween, perTileMs: 60 };
    if (tween.kind === 'lunge') tween = { ...tween, kind: 'flash', baseMs: 90 };
  }
  if (opts.reduceIntensity && tween.kind === 'burst') tween = { ...tween, kind: 'flash', baseMs: 90 };
  const clip: Clip = { entry, tween, ms: durationMs(tween, tiles) };
  if (tween.sound) clip.sound = tween.sound;
  return clip;
}

/** One act: the entries a cause produced in one stage (a Fireball's burst and the goblins it hit). */
const causeKey = (stage: Stage, c: Cause): string => {
  switch (c.kind) {
    case 'Intent': return `${stage}|i:${c.ref.heroId}:${c.ref.slot}`;
    case 'Declaration': return `${stage}|d:${c.actor}`;
    case 'Reaction': return `${stage}|r:${c.actor}:${c.idx}`;
    case 'Villain': return `${stage}|v:${c.leader}:${c.pulseSlot}`;
    case 'Event': return `${stage}|e:${c.card}`;
    case 'Snare': return `${stage}|s:${c.snare}`;
    case 'Tick': return `${stage}|tick`;
  }
};

/** Statuses a foe lays as a curse (docs/47: a hex, a chill, a mark). */
const CURSES: readonly StatusKind[] = ['Weakened', 'Slowed', 'Marked'];

/** Who an effect touches as the spell's target. */
const touched = (e: Effect): number | undefined =>
  e.kind === 'Damaged' || e.kind === 'Healed' || e.kind === 'StatusSet' || e.kind === 'Displaced' || e.kind === 'Revived' || e.kind === 'Hastened' ? e.unit : undefined;

/**
 * docs/47 SP3 — every act that is a spell gets its own clip before its entries: a hero's spell
 * (the act's `Cast`, i.e. `AbilityUsed`, names it; its server cues are anchored on that entry) or
 * a foe's (a spell's damage, a curse, a mend from a declaration; the first such entry's cues move
 * onto the spell clip). Squares are tracked through the log so a caster who walked first casts
 * from where it stopped.
 */
function spellActs(log: ResolutionLog, board: Snapshot | undefined): Map<number, { cast: SpellCast; entry: ResolutionEntry; act: string; foe: boolean }> {
  const where = new Map<number, Tile>();
  for (const u of board?.units ?? []) where.set(u.unitId, { ...u.tile });
  const kin = new Map((board?.minds ?? []).map((m) => [m.unit, m.kin]));
  const acts = new Map<string, ResolutionEntry[]>();
  for (const e of log.entries) { const k = causeKey(e.stage, e.cause); const l = acts.get(k) ?? []; l.push(e); acts.set(k, l); }
  // first entry seq of an act → the spell clip that goes before it
  const out = new Map<number, { cast: SpellCast; entry: ResolutionEntry; act: string; foe: boolean }>();
  const tilesAt = new Map<number, Map<number, Tile>>();   // seq → positions just before it
  for (const e of log.entries) {
    tilesAt.set(e.seq, new Map(where));
    const f = e.effect;
    if (f.kind === 'Moved' && f.path.length) where.set(f.unit, { ...f.path[f.path.length - 1]! });
    if (f.kind === 'Displaced') where.set(f.unit, { ...f.to });
  }
  for (const [act, entries] of acts) {
    const first = entries[0]!;
    const at = tilesAt.get(first.seq)!;
    const targets: number[] = [];
    for (const e of entries) { const u = touched(e.effect); if (u !== undefined && !targets.includes(u)) targets.push(u); }
    const targetTiles = targets.map((u) => at.get(u)).filter((t): t is Tile => !!t);
    const cast = entries.find((e) => e.effect.kind === 'Cast');
    if (cast && cast.effect.kind === 'Cast') {
      const look = lookFor(cast.effect.ability);
      if (!look) continue;
      const c: SpellCast = { look, caster: cast.effect.unit, from: cast.effect.from, tiles: cast.effect.tiles, targets, targetTiles, ability: cast.effect.ability };
      if (cast.effect.aim) c.aim = cast.effect.aim;
      if (cast.effect.ability >= ITEM_ABILITY_BASE) c.itemDef = cast.effect.ability - ITEM_ABILITY_BASE;
      out.set(first.seq, { cast: c, entry: cast, act, foe: false });
      continue;
    }
    // docs/72 IT18: a catalogue piece spent from the bag with no cast of its own still gets its look
    const used = entries.find((e) => e.effect.kind === 'ItemUsed');
    if (used && used.effect.kind === 'ItemUsed') {
      const look = itemDefLook(used.effect.def);
      const from = at.get(used.effect.unit);
      if (look && from) {
        const aimTile = at.get(used.effect.target) ?? targetTiles[0] ?? from;
        const c: SpellCast = { look, caster: used.effect.unit, from, aim: aimTile, tiles: [], targets: targets.length ? targets : [used.effect.target], targetTiles: targetTiles.length ? targetTiles : [aimTile], itemDef: used.effect.def };
        // docs/126: a foe's carried piece plays as a foe's
        out.set(first.seq, { cast: c, entry: used, act, foe: first.cause.kind === 'Declaration' });
        continue;
      }
    }
    // a foe's cast: its spell damage, else a curse, a mend or a ward it lays with no blow struck
    if (first.cause.kind === 'Intent' || first.cause.kind === 'Tick' || first.cause.kind === 'Event' || first.cause.kind === 'Snare') continue;
    const actor = first.actor;
    if (actor === undefined) continue;
    const blows = entries.filter((e) => e.effect.kind === 'Damaged');
    let kind: FoeCastKind | undefined;
    let trigger: ResolutionEntry | undefined;
    if (blows.length && blows.every((e) => e.effect.kind === 'Damaged' && e.effect.source === 'blast')) {
      kind = blows.length > 1 ? 'burst' : 'bolt'; trigger = blows[0];
    } else if (!blows.length) {
      trigger = entries.find((e) => (e.effect.kind === 'StatusSet' && CURSES.includes(e.effect.status)) || e.effect.kind === 'Healed' || (e.effect.kind === 'StatusSet' && (e.effect.status === 'Warded' || e.effect.status === 'Guarded')));
      if (trigger?.effect.kind === 'Healed') kind = 'mend';
      else if (trigger?.effect.kind === 'StatusSet') kind = CURSES.includes(trigger.effect.status) ? 'curse' : 'ward';
    }
    if (!kind || !trigger) continue;
    const from = tilesAt.get(trigger.seq)!.get(actor);
    if (!from) continue;
    const tt = targets.map((u) => tilesAt.get(trigger!.seq)!.get(u)).filter((t): t is Tile => !!t);
    const c: SpellCast = { look: foeLook(kind, kin.get(actor)), caster: actor, from, tiles: kind === 'burst' ? tt : [], targets, targetTiles: tt };
    if (tt[0]) c.aim = kind === 'burst' ? centreOf(tt) : tt[0];
    out.set(trigger.seq, { cast: c, entry: trigger, act, foe: true });
  }
  return out;
}

/** The square nearest the middle of `tiles` (a burst's heart when the log has no aim). */
function centreOf(tiles: Tile[]): Tile {
  const x = tiles.reduce((a, t) => a + t.x, 0) / tiles.length, y = tiles.reduce((a, t) => a + t.y, 0) / tiles.length;
  return tiles.reduce((best, t) => (Math.hypot(t.x - x, t.y - y) < Math.hypot(best.x - x, best.y - y) ? t : best), tiles[0]!);
}

/** The spell clip: footprint = the caster, its targets and its squares. */
function spellClip(cast: SpellCast, entry: ResolutionEntry, opts: PlanOptions): Clip {
  const { ms, impactMs } = spellClipMs(cast, opts.reduceMotion);
  return { entry, tween: { kind: 'spell', baseMs: ms, perTileMs: 0 }, ms, spell: cast, impactMs };
}

/** docs/56 EP1: a clip that lands something on a unit — only one of these plays at a time. */
const isBlow = (clip: Clip): boolean => !!clip.spell || clip.tween.kind === 'lunge' || clip.tween.kind === 'projectile' || clip.tween.kind === 'burst' || clip.tween.kind === 'sink'
  || (clip.entry.effect.kind === 'Damaged') || clip.entry.effect.kind === 'Healed' || clip.entry.effect.kind === 'Dropped';

interface Print { units: Set<number>; tiles: Set<string>; blow: boolean }
const printOf = (entry: ResolutionEntry, blow: boolean, extra?: SpellCast): Print => {
  const f = footprint(entry.effect);
  const units = new Set([...f.units, ...actorOf(entry)]);
  const tiles = new Set(f.tiles.map((t) => `${t.x},${t.y}`));
  // docs/140: a trap's moments play in their order — the card, then each act, then what it did
  const snare = snareKey(entry);
  if (snare !== undefined) tiles.add(`snare:${snare}`);
  if (extra) { units.add(extra.caster); for (const u of extra.targets) units.add(u); for (const t of extra.tiles) tiles.add(`${t.x},${t.y}`); }
  return { units, tiles, blow };
};
const clash = (a: Print, b: Print) => (a.blow && b.blow) || [...b.units].some((u) => a.units.has(u)) || [...b.tiles].some((t) => a.tiles.has(t));

/** docs/140: the trap an entry belongs to (its own effects, or ones it caused). */
function snareKey(entry: ResolutionEntry): number | undefined {
  const e = entry.effect;
  if (e.kind === 'SnareSprung' || e.kind === 'SnareActed' || e.kind === 'SnareEnded' || e.kind === 'SnareFound' || e.kind === 'SnareDisarmed') return e.snare;
  return entry.cause.kind === 'Snare' ? entry.cause.snare : undefined;
}

/** Party stages vs the foes' (04 P4: the party acts, then the foe declares and acts, then events). */
const sideOf = (stage: Stage): 'party' | 'foes' => (stage === 'VillainAction' || stage === 'Enemy' ? 'foes' : 'party');

/**
 * docs/56 EP5: the seq of the drop that leaves no hero standing (heroes downed one by one through
 * the log; a revive stands one back up), or undefined while anyone is still up.
 */
export function finalFallSeq(log: ResolutionLog, board: Snapshot | undefined): number | undefined {
  if (!board) return undefined;
  const up = new Set(board.units.filter((u) => u.heroId !== undefined && u.onBoard !== false && !u.downed).map((u) => u.unitId));
  if (!up.size) return undefined;
  let last: number | undefined;
  for (const e of log.entries) {
    const f = e.effect;
    if (f.kind === 'Dropped' && up.has(f.unit)) { up.delete(f.unit); if (!up.size) last = e.seq; }
    if (f.kind === 'Revived' && board.units.some((u) => u.unitId === f.unit && u.heroId !== undefined)) { up.add(f.unit); last = undefined; }
  }
  return up.size ? undefined : last;
}

/**
 * docs/102 FL2, FL5 — which moves are flights: every Moved of a unit whose appearance flies. The
 * bounds that follow it straight on in the same act (a stoop that then leaps on) fly on in the same
 * flight, and a shove of another figure that follows it in the same act (the great eagle's Carry
 * off pulls its prey after it) is carried in its talons. Returns the flight per head entry and the
 * seqs it drew.
 */
export function flightsOf(log: ResolutionLog, board: Snapshot | undefined): { heads: Map<number, NonNullable<Clip['flight']>>; drawn: Set<number> } {
  const heads = new Map<number, NonNullable<Clip['flight']>>();
  const drawn = new Set<number>();
  if (!board) return { heads, drawn };
  const look = new Map(board.units.map((u) => [u.unitId, u.appearance]));
  const where = new Map(board.units.map((u) => [u.unitId, { ...u.tile }]));
  const es = log.entries;
  const moved = (e: Effect) => { if (e.kind === 'Moved' && e.path.length) where.set(e.unit, { ...e.path[e.path.length - 1]! }); if (e.kind === 'Displaced') where.set(e.unit, { ...e.to }); };
  for (let i = 0; i < es.length; i++) {
    const head = es[i]!, f = head.effect;
    if (drawn.has(head.seq) || f.kind !== 'Moved' || !isFlight(f, look.get(f.unit))) { moved(f); continue; }
    const from = f.from ?? where.get(f.unit) ?? f.path[0]!;
    const key = causeKey(head.stage, head.cause);
    let to = f.path[f.path.length - 1]!;
    let j = i + 1;
    for (; j < es.length; j++) {
      const n = es[j]!, g = n.effect;
      if (g.kind !== 'Moved' || g.unit !== f.unit || causeKey(n.stage, n.cause) !== key || !isFlight(g, look.get(g.unit))) break;
      to = g.path[g.path.length - 1]!;
      drawn.add(n.seq);
    }
    const flight: NonNullable<Clip['flight']> = { from, to };
    const n = es[j];
    if (n && n.effect.kind === 'Displaced' && n.effect.unit !== f.unit && causeKey(n.stage, n.cause) === key) {
      const d = n.effect;
      const went = Math.abs(d.to.x - d.from.x) + Math.abs(d.to.y - d.from.y);
      if (went > 0 && went <= WARP_SQUARES) { flight.carry = { unit: d.unit, from: { ...d.from }, to: { ...d.to } }; drawn.add(n.seq); }
    }
    heads.set(head.seq, flight);
    for (let k = i; k < Math.max(i + 1, j); k++) moved(es[k]!.effect);
    i = j - 1;
  }
  return { heads, drawn };
}

export function buildPlan(log: ResolutionLog, opts: PlanOptions = {}): AnimationPlan {
  const spells = spellActs(log, opts.board);
  // docs/102 FL2: under reduce motion a flier slides like everyone else
  const flights = opts.reduceMotion ? { heads: new Map<number, NonNullable<Clip['flight']>>(), drawn: new Set<number>() } : flightsOf(log, opts.board);
  const sizeOf = new Map((opts.board?.units ?? []).map((u) => [u.unitId, u.size ?? 1]));
  const spellOf = new Map<string, { foe: boolean; blink: boolean; entry: ResolutionEntry }>();
  for (const s of spells.values()) spellOf.set(s.act, { foe: s.foe, blink: s.cast.look.form === 'blink', entry: s.entry });
  const finalFall = finalFallSeq(log, opts.board);
  // docs/66 RM10: a call's or a raising's card is spoken by its own entry (Called, Raised, or
  // broken), whose banner names who comes; the card's name in a banner too would say it twice
  const spoken = new Set(log.entries.filter((e) => e.effect.kind === 'Called' || e.effect.kind === 'Raised' || e.effect.kind === 'CallBroken' || e.effect.kind === 'RaiseBroken').map((e) => causeKey(e.stage, e.cause)));
  const stages: StageBlock[] = [];
  // docs/60: the boundary's own entries are bookkeeping, but a crawl's settle (a chamber or a depth
  // entered, a lair woken, a roamer finding the party) is logged there and is shown last
  for (const stage of [...STAGE_ORDER, 'Boundary'] as const) {
    const entries = log.entries.filter((e) => e.stage === stage && (stage !== 'Boundary' || isCrawlEffect(e.effect) || isExploreEffect(e.effect) || isSnareEntry(e)));
    if (entries.length === 0) continue;
    const groups: Clip[][] = [];
    const prints: Print[][] = [];
    const place = (clip: Clip, print: Print) => {
      // Place in the first group (from the last) that it does not overlap, without passing a group it does overlap.
      let target = -1;
      for (let g = prints.length - 1; g >= 0; g--) {
        if (prints[g]!.some((p) => clash(p, print))) break;
        target = g;
      }
      if (target === -1) { prints.push([print]); groups.push([clip]); }
      else { prints[target]!.push(print); groups[target]!.push(clip); }
    };
    for (const entry of entries) {
      const s = spells.get(entry.seq);
      if (s) place(spellClip(s.cast, s.entry, opts), printOf(entry, true, s.cast));
      const act = spellOf.get(causeKey(entry.stage, entry.cause));
      // the spell clip stands for its Cast entry; a Wall of Fire's flames (card 0) and a snare are drawn by it
      if (entry.effect.kind === 'EventFired' && spoken.has(causeKey(entry.stage, entry.cause))) continue;
      if (act && (entry.effect.kind === 'Cast' || (entry.effect.kind === 'EventFired' && entry.effect.card === 0) || (entry.effect.kind === 'Other' && entry.effect.name === 'TrapSet'))) continue;
      let clip = clipFor(entry, opts);
      if (act) clip = afterSpell(clip, act.blink);
      if (act?.foe && act.entry === entry) clip = { ...clip, muted: true };
      if (entry.seq === finalFall && !opts.reduceMotion) clip = { ...clip, finalFall: true, ms: Math.max(clip.ms, FINAL_FALL_MS) };
      // docs/102: a flight's later bounds and the figure it carried off were drawn by it (their cues too)
      if (flights.drawn.has(entry.seq)) clip = { ...clip, tween: { kind: 'none', baseMs: 0, perTileMs: 0 }, ms: 1, drawn: true, muted: true };
      const flight = flights.heads.get(entry.seq);
      if (flight && !clip.teleport) {
        const size = entry.effect.kind === 'Moved' ? sizeOf.get(entry.effect.unit) ?? 1 : 1;
        clip = { ...clip, flight, ms: flightMs(crowSquares(flight.from, flight.to), size) };
      }
      // the blow the spell clip already landed is its aftermath, not a second blow
      const print = printOf(entry, isBlow(clip) && !act);
      // the figure it carries off is in its flight: nothing else may move it meanwhile
      if (clip.flight?.carry) { print.units.add(clip.flight.carry.unit); for (const t of [clip.flight.carry.from, clip.flight.carry.to]) print.tiles.add(`${t.x},${t.y}`); }
      place(clip, print);
    }
    const side = sideOf(stage);
    const gaps = groups.map((g) => (g.some((c) => isBlow(c)) ? GROUP_GAP_MS : 0));
    const idle = groups.every((g) => g.every((c) => c.tween.kind === 'none'));
    // the beat goes before the other side's first stage that shows something
    const prev = [...stages].reverse().find((s) => !s.idle);
    stages.push({ stage, groups, gaps, side, idle, leadMs: !idle && prev && prev.side !== side ? SIDE_GAP_MS : 0 });
  }
  const totalMs = stages.reduce((sum, s) => sum + s.leadMs + s.groups.reduce((g, clips, i) => g + Math.max(0, ...clips.map((c) => c.ms)) + (s.gaps[i] ?? 0), 0), 0);
  const autoSped = opts.speed === undefined && totalMs > AUTO_2X_THRESHOLD_MS;
  const speed: Speed = opts.speed ?? (autoSped ? 2 : 1);
  return { stages, totalMs, speed, autoSped };
}

/** What follows a spell clip is quick: the blows ripple through the burst, the statuses pop in. */
function afterSpell(clip: Clip, blink: boolean): Clip {
  const e = clip.entry.effect;
  if (e.kind === 'Moved' && blink) return { ...clip, tween: { kind: 'slide', baseMs: 1, perTileMs: 0 }, ms: 1, teleport: true };
  if (e.kind === 'Damaged') return { ...clip, tween: { ...clip.tween, kind: 'flash', baseMs: 130, perTileMs: 0 }, ms: 130 };
  if (e.kind === 'Healed' || e.kind === 'StatusSet') return { ...clip, tween: { ...clip.tween, kind: 'pulse', baseMs: 170, perTileMs: 0 }, ms: 170 };
  return clip;
}

/** The party's turn ends before this stage: the first foes' stage that shows something (stages.length if none). */
export function foesFrom(plan: AnimationPlan): number {
  const i = plan.stages.findIndex((s) => s.side === 'foes' && !s.idle);
  return i < 0 ? plan.stages.length : i;
}

/**
 * The pulse was only walking: no foe acted and nothing but steps showed (no blow, search, mechanism,
 * card or number), so the next turn opens without the settle.
 */
export function onlyWalked(plan: AnimationPlan): boolean {
  return foesFrom(plan) === plan.stages.length
    && plan.stages.every((s) => s.groups.every((g) => g.every((c) => !c.spell && (c.tween.kind === 'none' || (c.entry.effect.kind === 'Moved' && c.tween.kind === 'slide')))));
}

/** Wall-clock duration at the plan's speed. */
export function playingMs(plan: AnimationPlan): number { return Math.round(plan.totalMs / plan.speed); }
